import { CopyDuplicateObject, Trash } from '@griply/assets';
import { Task, TaskType, Timestamp } from '@griply/types';
import { getWeekStartsOn } from '@griply/utils';
import {
  buildDateUpdateTasks,
  useDeleteTask,
  useDuplicateTasks,
  useUpdateTasks,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

export type RemoveListProps = {
  task: Task;
  onClose: () => void;
  hasListBelow?: boolean;
  onAction?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  occurrenceDate?: Timestamp;
};

export const LifecycleList: React.FC<RemoveListProps> = ({
  task,
  onClose,
  hasListBelow,
  onAction,
  onDelete: customOnDelete,
  onDuplicate: customOnDuplicate,
  occurrenceDate,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const parentTaskId = useMemo(
    () => task.parentIds?.[task.parentIds.length - 1],
    [task],
  );

  const duplicateTask = useDuplicateTasks(parentTaskId);
  const { submit: deleteTask } = useDeleteTask(parentTaskId);
  const { submit: updateTasks } = useUpdateTasks();

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);

  const onDuplicate = () => {
    if (customOnDuplicate) {
      customOnDuplicate();
      onAction?.();
      onClose();
      return;
    }

    duplicateTask([task]);
    onAction?.();
    onClose();
  };

  const onDelete = () => {
    if (customOnDelete) {
      customOnDelete();
      onAction?.();
      onClose();
      return;
    }

    deleteTask(task);
    onAction?.();
    onClose();
  };

  const onDeleteOccurrence = () => {
    const updates = buildDateUpdateTasks([task], undefined, occurrenceDate, {
      weekStartsOn,
      today,
    });

    if (updates.length) {
      updateTasks(updates);
    }

    onAction?.();
    onClose();
  };

  if (isHabit) {
    if (!occurrenceDate) {
      return null;
    }

    return (
      <PopupMenuList hasBottomBorder={hasListBelow}>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDeleteOccurrence}
            start={<Icon icon={Trash} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
          >
            {t('context-menus.task.labels.timeslot.occurrence.remove')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    );
  }

  return (
    <PopupMenuList hasBottomBorder={hasListBelow}>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDuplicate}
          start={<Icon icon={CopyDuplicateObject} />}
        >
          {t('context-menus.task.labels.duplicate')}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDelete}
          start={<Icon icon={Trash} />}
          startColor={Colors.Error}
          textColor={Colors.Error}
        >
          {t('context-menus.task.labels.delete')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
